<template>
  <div class="quiz-editor">
    <div v-if="!hasQuestions" class="quiz-editor__empty-state">
      <div class="quiz-editor__empty-inner">
        <div class="quiz-editor__empty-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <p class="quiz-editor__empty-title">No questions found</p>
        <p class="quiz-editor__empty-description">
          At least one question is required to test knowledge for this checkpoint
        </p>
        <div v-if="!readonly" class="quiz-editor__empty-actions">
          <StandardButton
            type="magenta"
            variant="primary"
            label="Add Question"
            left-icon="plusLeft"
            :disabled="isQuizEditorBusy"
            @click="openAddQuestionModal"
          />
          <AddORAskIQButton
            :show-add-button="false"
            :show-a-i-button="true"
            label="Suggest"
            emp-label="Add"
            @addAI="emit('suggest', $event)"
          />
        </div>
      </div>
    </div>

    <template v-else>
      <div class="quiz-editor__filled">
        <div v-if="!readonly && !hideToolbar" class="quiz-editor__toolbar">
          <div class="quiz-editor__toolbar-right">
            <EXTooltip
              v-if="isQuizAtQuestionLimit"
              :title="QUIZ_MAX_QUESTIONS_TOOLTIP"
              placement="top"
              :delay="200"
            >
              <span class="quiz-editor__disabled-btn-tooltip-wrap">
                <StandardButton
                  type="magenta"
                  variant="secondary"
                  size="small"
                  label="Add Question"
                  left-icon="plusLeft"
                  disabled
                />
              </span>
            </EXTooltip>
            <StandardButton
              v-else
              type="magenta"
              variant="secondary"
              size="small"
              label="Add Question"
              left-icon="plusLeft"
              :disabled="isQuizEditorBusy"
              @click="openAddQuestionModal"
            />
            <AddORAskIQButton
              :show-add-button="false"
              :show-a-i-button="true"
              label="Suggest"
              emp-label="Add"
              @addAI="emit('suggest', $event)"
            />
            <MenuIcon
              v-if="hasQuestions"
              class="quiz-editor__toolbar-menu"
              :vertical="true"
              :dropdown-items="quizBulkMenuItems"
              :show-tooltip="false"
              :width="240"
              @onClick="onQuizBulkMenuClick"
            />
          </div>
        </div>

        <div
          ref="quizQuestionListScrollRef"
          class="quiz-editor__question-list-scroll"
          @dragover.prevent="onQuizListNativeDragOver"
          @mousedown="onQuizQuestionListMouseDown"
          @selectstart="onQuizQuestionSelectStart"
        >
          <draggable
            ref="quizDraggableRef"
            :list="orderedRawQuestions"
            tag="div"
            draggable=".quiz-editor__q-row"
            filter=".quiz-editor__card-actions, .quiz-editor__card-actions *"
            :prevent-on-filter="true"
            :class="[
              'quiz-editor__question-list',
              { 'quiz-editor__question-list--dragging': questionDragActive },
            ]"
            :animation="150"
            easing="cubic-bezier(0.25, 0.8, 0.25, 1)"
            direction="vertical"
            :swap-threshold="0.5"
            :force-fallback="true"
            :fallback-on-body="true"
            :fallback-tolerance="5"
            :empty-insert-threshold="12"
            :scroll="true"
            :scroll-sensitivity="100"
            :scroll-speed="20"
            :bubble-scroll="true"
            :scroll-fn="quizSortableScrollFn"
            ghost-class="quiz-editor__question-ghost"
            drag-class="quiz-editor__q-row--picked-active"
            :move="onQuizQuestionSortMove"
            :disabled="readonly || isQuizEditorBusy"
            @start="onQuestionDragStart"
            @end="onQuestionDragEnd"
          >
            <div
              v-for="(question, questionIndex) in normalizedQuestions"
              :key="question.__quizRowKey"
              class="quiz-editor__q-row"
              :class="{ 'quiz-editor__q-row--readonly': readonly }"
              :data-quiz-row-key="String(question.__quizRowKey)"
            >
              <div v-if="!readonly" class="quiz-editor__drag-strip">
                <button
                  type="button"
                  class="quiz-editor__drag-handle"
                  aria-label="Reorder question"
                >
                  <img
                    class="quiz-editor__drag-grip"
                    :src="quizReorderGripSrc"
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />
                </button>
              </div>
              <div class="quiz-editor__card">
                <div class="quiz-editor__card-head">
                  <span class="quiz-editor__card-label">Question</span>
                  <div v-if="!readonly" class="quiz-editor__card-actions">
                    <button
                      type="button"
                      class="quiz-editor__icon-btn quiz-editor__icon-btn--edit"
                      aria-label="Edit question"
                      :disabled="isQuizEditorBusy"
                      @click="openEditQuestionModal(questionIndex)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        aria-hidden="true"
                        class="quiz-editor__icon-btn-img"
                      >
                        <path
                          d="M8.75003 2.91403L11.0836 5.24733M12.3518 3.97124C12.6602 3.66291 12.8336 3.24469 12.8336 2.80859C12.8337 2.37249 12.6605 1.95423 12.3521 1.64582C12.0437 1.33741 11.6255 1.16412 11.1893 1.16406C10.7532 1.16401 10.3349 1.3372 10.0264 1.64553L2.24057 9.43231C2.10512 9.56735 2.00495 9.73361 1.94888 9.91647L1.17823 12.4551C1.16315 12.5055 1.16201 12.5591 1.17493 12.6102C1.18785 12.6612 1.21435 12.7078 1.25162 12.745C1.28888 12.7822 1.33552 12.8086 1.38659 12.8215C1.43766 12.8343 1.49125 12.8331 1.54168 12.8179L4.08115 12.0479C4.26385 11.9924 4.43012 11.8928 4.56536 11.758L12.3518 3.97124Z"
                          stroke="#344054"
                          stroke-width="2"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="quiz-editor__icon-btn quiz-editor__icon-btn--delete"
                      aria-label="Delete question"
                      :disabled="isQuizEditorBusy || question.q_no == null || question.q_no === ''"
                      @click="openDeleteQuestionDialog(question.q_no)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="16"
                        viewBox="0 0 14 16"
                        fill="none"
                        aria-hidden="true"
                        class="quiz-editor__icon-btn-img"
                      >
                        <path
                          d="M11.6667 3.66688V13.001C11.6667 13.3546 11.5262 13.6938 11.2761 13.9438C11.0261 14.1939 10.687 14.3344 10.3333 14.3344H3.66667C3.31304 14.3344 2.97391 14.1939 2.72386 13.9438C2.47381 13.6938 2.33333 13.3546 2.33333 13.001V3.66688M1 3.66688H13M4.33333 3.66688V2.33344C4.33333 1.97979 4.47381 1.64062 4.72386 1.39056C4.97391 1.14049 5.31304 1 5.66667 1H8.33333C8.68696 1 9.02609 1.14049 9.27614 1.39056C9.52619 1.64062 9.66667 1.97979 9.66667 2.33344V3.66688"
                          stroke="#D92D20"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="quiz-editor__card-divider" role="presentation" />
                <div class="quiz-editor__card-body">
                  <p class="quiz-editor__card-question">{{ question.text }}</p>
                  <ul class="quiz-editor__card-answers">
                    <li
                      v-for="(answer, answerIndex) in question.answers"
                      :key="`quiz-editor-a-${questionIndex}-${answerIndex}`"
                      class="quiz-editor__card-answer"
                      :class="{
                        'quiz-editor__card-answer--correct':
                          answer.isExpected && resolvedQuizMode !== 'SKIP_INSPECTION',
                      }"
                    >
                      <span
                        v-if="answer.isExpected"
                        class="quiz-editor__card-answer-check"
                        aria-hidden="true"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="#101828"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </span>
                      <span v-else class="quiz-editor__card-answer-spacer" />
                      <span class="quiz-editor__card-answer-text">{{ answer.text }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </div>
    </template>

    <EditQuestionModal
      :dialog="editQuestionDialogOpen"
      :initial-question="editModalInitialQuestion"
      :initial-answers="editModalInitialAnswers"
      :is-submitting="editQuestionSubmitInProgress"
      @update:modelValue="onEditQuestionDialogOpenChange"
      @input="onEditQuestionDialogOpenChange"
      @close="onEditQuestionModalClose"
      @save="onEditQuestionModalSave"
    />

    <AddQuestionModal
      :dialog="addQuestionDialogOpen"
      :is-submitting="addQuestionSubmitInProgress"
      @update:modelValue="onAddQuestionDialogOpenChange"
      @input="onAddQuestionDialogOpenChange"
      @close="onAddQuestionModalClose"
      @save="onAddQuestionModalSave"
    />

    <DeleteQuestionDialog
      :dialog="deleteQuestionDialogOpen"
      :submitting="deleteQuestionInProgress"
      @close="onDeleteQuestionDialogClose"
      @confirm="onDeleteQuestionDialogConfirm"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import draggable from "vuedraggable";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import AddORAskIQButton from "@components/AddORAskIQButton/AddORAskIQButton.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import AddQuestionModal from "@components/AddQuestionModal/AddQuestionModal.vue";
import EditQuestionModal from "@components/EditQuestionModal/EditQuestionModal.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import { MAX_QUIZ_QUESTIONS } from "@components/constants/quizLimits";
import DeleteQuestionDialog from "@components/DeleteQuestionDialog/DeleteQuestionDialog.vue";

const props = defineProps({
  questions: {
    type: Array,
    default: null,
  },
  /**
   * Video-to-components / IQ API shape, e.g.
   * `{ quiz_title, questions: [{ q_no, q_text, q_answers: [{ a_no, a_text, isCorrect, correct_description }] }] }`
   * When present with questions, it takes precedence over `questions`.
   */
  quiz: {
    type: Object,
    default: null,
  },
  /**
   * Completion mode from parent (Both | Skip inspection | Skip quiz).
   * Used only for answer highlighting; mode UI lives in CheckpointEditFormV2.
   */
  quizMode: {
    type: String,
    default: null,
    validator: (v) => v == null || ["BOTH_REQUIRED", "SKIP_INSPECTION", "SKIP_QUIZ"].includes(v),
  },
  /** When true, hide Add Question / Suggest and other edit actions (e.g. checkpoint not locked by self). */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** When true, hide the filled-state toolbar (parent renders Add Question / Suggest on the quiz-mode row). */
  hideToolbar: {
    type: Boolean,
    default: false,
  },
});

const quizReorderGripSrc = require("@components/assets/GridDots.svg");

const emit = defineEmits([
  "add-question",
  "suggest",
  /** Delete: `{ q_no, complete }`. Update: `{ index, q_no, question, answers, complete }` (`index` optional for hosts). */
  "delete-question",
  "reorder-questions",
  "update-question",
  /** `{ action: 'delete-quiz' }` — parent shows confirm dialog; API not wired in parent yet */
  "quiz-bulk-action",
]);

const resolvedQuizMode = computed(() => {
  if (
    props.quizMode === "BOTH_REQUIRED" ||
    props.quizMode === "SKIP_INSPECTION" ||
    props.quizMode === "SKIP_QUIZ"
  ) {
    return props.quizMode;
  }
  return "BOTH_REQUIRED";
});

const addQuestionDialogOpen = ref(false);
/** Add-question: wait for API before closing dialog (parent signals via `complete` callback). */
const addQuestionSubmitInProgress = ref(false);
/** Edit-question: Save shows loader until parent calls `complete`. */
const editQuestionSubmitInProgress = ref(false);
/** Delete-question: dialog shows submitting until parent calls `complete`; disable edit/delete/reorder meanwhile. */
const deleteQuestionInProgress = ref(false);
const deleteQuestionDialogOpen = ref(false);
/** Backend `q_no` pending after `DeleteQuestionDialog` confirm */
const pendingDeleteQNo = ref(null);
const editQuestionDialogOpen = ref(false);
const editModalInitialQuestion = ref("");
const editModalInitialAnswers = ref([]);
const editingQuestionIndex = ref(-1);
/** True while a question row is being reordered (Sortable active). */
const questionDragActive = ref(false);
/** Index where the drag started — used to collapse the in-list gap until another slot is targeted. */
const questionDragFromIndex = ref(-1);
/** Snapshot of `orderedRawQuestions` at drag start — restored when order does not change (e.g. drop outside). */
const questionOrderSnapshot = ref(null);
/** Measured list-row size at drag start (before ghost collapse). */
let quizDragFloaterMetrics = null;
/** Last pointer position while dragging (for valid drop vs cancel-outside). */
let lastQuizDragPointer = { x: 0, y: 0 };
/** Scroll region wrapping the question list (Sortable auto-scroll targets this). */
const quizQuestionListScrollRef = ref(null);
const quizDraggableRef = ref(null);

/** Sortable `scrollFn` — apply scroll deltas to our list panel when that layer is the scroll root. */
function quizSortableScrollFn(scrollOffsetX, scrollOffsetY, _evt, _touchEvt, autoScrollEl) {
  const wrap = quizQuestionListScrollRef.value;
  if (wrap && autoScrollEl === wrap) {
    wrap.scrollTop += scrollOffsetY;
    wrap.scrollLeft += scrollOffsetX;
    return;
  }
  return "continue";
}

/** Native HTML5 drag: Sortable often does not scroll inner `overflow:auto` panels in Chrome — nudge `scrollTop` from drag position. */
function onQuizListNativeDragOver(event) {
  if (!questionDragActive.value) return;
  const wrap = quizQuestionListScrollRef.value;
  if (!wrap) return;
  const rect = wrap.getBoundingClientRect();
  const x = event.clientX;
  const y = event.clientY;
  if (x < rect.left - 24 || x > rect.right + 24) return;

  const zone = 90;
  const maxStep = 28;
  if (y < rect.top + zone) {
    const dist = rect.top + zone - y;
    wrap.scrollTop -= Math.min(maxStep, 10 + Math.floor(dist / 5));
  } else if (y > rect.bottom - zone) {
    const dist = y - (rect.bottom - zone);
    wrap.scrollTop += Math.min(maxStep, 10 + Math.floor(dist / 5));
  }
}

function onQuizWindowDragOver(event) {
  if (!questionDragActive.value) return;
  lastQuizDragPointer = { x: event.clientX, y: event.clientY };
  onQuizListNativeDragOver(event);
  const listEl = quizQuestionListScrollRef.value?.querySelector?.(".quiz-editor__question-list");
  if (listEl) syncQuizQuestionGhostGap({ to: listEl });
}

function clearDocumentTextSelection() {
  const sel = window.getSelection?.();
  if (!sel) return;
  if (sel.removeAllRanges) sel.removeAllRanges();
  else if (sel.empty) sel.empty();
}

function setQuizDragSelectLock(locked) {
  const value = locked ? "none" : "";
  document.body.style.userSelect = value;
  document.body.style.webkitUserSelect = value;
}

/** Stop browser text selection while initiating drag on a question row. */
function onQuizQuestionListMouseDown(event) {
  if (isQuizEditorBusy.value) return;
  if (event.target.closest?.(".quiz-editor__card-actions")) return;
  if (!event.target.closest?.(".quiz-editor__q-row")) return;
  clearDocumentTextSelection();
}

function onQuizQuestionSelectStart(event) {
  if (event.target.closest?.(".quiz-editor__card-actions")) return;
  if (event.target.closest?.(".quiz-editor__q-row")) {
    event.preventDefault();
  }
}

let nextQuizRowKey = 1;

function assignQuizRowKeys(items) {
  return items.map((item) => ({
    ...item,
    __quizRowKey: item.__quizRowKey ?? nextQuizRowKey++,
  }));
}

function stripQuizRowKey(item) {
  if (!item || typeof item !== "object") return item;
  const { __quizRowKey, ...rest } = item;
  return rest;
}

const orderedRawQuestions = ref([]);

/** Unwrap `{ data: { quiz } }` or `{ quiz }` to the inner quiz object */
function resolveQuizObject(quizProp) {
  if (!quizProp || typeof quizProp !== "object") return null;
  if (Array.isArray(quizProp.questions)) return quizProp;
  if (quizProp.data?.quiz && typeof quizProp.data.quiz === "object") return quizProp.data.quiz;
  if (quizProp.quiz && typeof quizProp.quiz === "object") return quizProp.quiz;
  return null;
}

/** Map API `quiz` payload to internal rows used by draggable + modals */
function normalizeApiQuizToQuestionRows(quizPayload) {
  const resolved = resolveQuizObject(quizPayload) ?? quizPayload;
  if (!resolved || typeof resolved !== "object") return [];
  const list = Array.isArray(resolved.questions) ? resolved.questions : [];
  return list.map((q) => {
    const qText = q.q_text ?? q.question ?? q.text ?? "";
    const rawAnswers = Array.isArray(q.q_answers)
      ? q.q_answers
      : Array.isArray(q.answers)
        ? q.answers
        : [];
    const answers = rawAnswers.map((a) => {
      const text = a.a_text ?? a.text ?? a.answer ?? "";
      const correct =
        a.isCorrect === 1 ||
        a.isCorrect === true ||
        a.is_correct === true ||
        Number(a.isCorrect) === 1;
      return {
        text,
        answer: text,
        a_text: text,
        is_correct: correct,
        correct_description: a.correct_description ?? "",
      };
    });
    return {
      question: qText,
      text: qText,
      answers,
      q_no: q.q_no,
    };
  });
}

const quizTitle = computed(() => {
  const resolved = resolveQuizObject(props.quiz) ?? props.quiz;
  const t = resolved?.quiz_title;
  return typeof t === "string" && t.trim() ? t.trim() : "";
});

watch(
  () => [props.quiz, props.questions],
  () => {
    if (questionDragActive.value) return;
    const fromQuiz = normalizeApiQuizToQuestionRows(props.quiz);
    if (fromQuiz.length > 0) {
      orderedRawQuestions.value = assignQuizRowKeys([...fromQuiz]);
      return;
    }
    const q = props.questions;
    if (!Array.isArray(q) || q.length === 0) {
      orderedRawQuestions.value = [];
      return;
    }
    orderedRawQuestions.value = assignQuizRowKeys([...q]);
  },
  { immediate: true, deep: true }
);

function openAddQuestionModal() {
  if (
    deleteQuestionInProgress.value ||
    editQuestionSubmitInProgress.value ||
    isQuizAtQuestionLimit.value
  )
    return;
  addQuestionDialogOpen.value = true;
}

function onAddQuestionDialogOpenChange(open) {
  if (!open && addQuestionSubmitInProgress.value) return;
  addQuestionDialogOpen.value = open;
}

function onAddQuestionModalClose() {
  if (addQuestionSubmitInProgress.value) return;
  addQuestionDialogOpen.value = false;
}

function onAddQuestionModalSave(payload) {
  addQuestionSubmitInProgress.value = true;
  emit("add-question", {
    question: payload.question,
    answers: payload.answers,
    complete: (err) => {
      addQuestionSubmitInProgress.value = false;
      if (!err) {
        addQuestionDialogOpen.value = false;
      }
    },
  });
}

function defaultModalAnswers() {
  return [
    { answer: "", checked: false, sequence: 1 },
    { answer: "", checked: false, sequence: 2 },
    { answer: "", checked: false, sequence: 3 },
    { answer: "", checked: false, sequence: 4 },
  ];
}

function rawQuestionToModalAnswers(raw) {
  const answerList = Array.isArray(raw.answers)
    ? raw.answers
    : Array.isArray(raw.q_answers)
      ? raw.q_answers
      : Array.isArray(raw.options)
        ? raw.options
        : [];

  const expectedIndexes = Array.isArray(raw.correctAnswers)
    ? raw.correctAnswers.filter((item) => Number.isInteger(item))
    : [];

  const expectedAnswerTexts = Array.isArray(raw.correctAnswers)
    ? raw.correctAnswers.filter((item) => typeof item === "string")
    : [];

  const rows = answerList.map((answer, index) => {
    const answerText = extractAnswerText(answer);
    const checked = answerIsMarkedCorrect(
      answer,
      index,
      expectedIndexes,
      expectedAnswerTexts,
      answerText
    );
    return {
      answer: answerText,
      checked,
      sequence: index + 1,
    };
  });

  return rows.length > 0 ? rows : defaultModalAnswers();
}

function resolveRowQNo(row) {
  if (!row || typeof row !== "object") return null;
  const n = row.q_no;
  if (n == null || n === "") return null;
  return n;
}

function openDeleteQuestionDialog(qNo) {
  if (
    deleteQuestionInProgress.value ||
    addQuestionSubmitInProgress.value ||
    editQuestionSubmitInProgress.value
  )
    return;
  if (qNo == null || qNo === "") return;
  pendingDeleteQNo.value = qNo;
  deleteQuestionDialogOpen.value = true;
}

function onDeleteQuestionDialogClose() {
  if (deleteQuestionInProgress.value) return;
  deleteQuestionDialogOpen.value = false;
  pendingDeleteQNo.value = null;
}

function onDeleteQuestionDialogConfirm() {
  const qNo = pendingDeleteQNo.value;
  if (qNo == null || qNo === "") return;
  if (deleteQuestionInProgress.value) return;
  deleteQuestionInProgress.value = true;
  emit("delete-question", {
    q_no: qNo,
    complete: (err) => {
      deleteQuestionInProgress.value = false;
      if (!err) {
        deleteQuestionDialogOpen.value = false;
        pendingDeleteQNo.value = null;
      }
    },
  });
}

function openEditQuestionModal(index) {
  if (deleteQuestionInProgress.value || editQuestionSubmitInProgress.value) return;
  const raw = orderedRawQuestions.value[index];
  if (!raw) return;
  editingQuestionIndex.value = index;
  editModalInitialQuestion.value = raw.question || raw.text || raw.q_text || "";
  editModalInitialAnswers.value = rawQuestionToModalAnswers(raw);
  editQuestionDialogOpen.value = true;
}

function onEditQuestionDialogOpenChange(open) {
  if (!open && editQuestionSubmitInProgress.value) return;
  editQuestionDialogOpen.value = open;
  if (!open) {
    editingQuestionIndex.value = -1;
  }
}

function onEditQuestionModalClose() {
  if (editQuestionSubmitInProgress.value) return;
  editQuestionDialogOpen.value = false;
  editingQuestionIndex.value = -1;
}

function onEditQuestionModalSave(payload) {
  const index = editingQuestionIndex.value;
  if (index < 0) return;
  const raw = orderedRawQuestions.value[index];
  const q_no = resolveRowQNo(raw);
  if (q_no == null || q_no === "") {
    return;
  }
  editQuestionSubmitInProgress.value = true;
  emit("update-question", {
    index,
    q_no,
    question: payload.question,
    answers: payload.answers,
    complete: (err) => {
      editQuestionSubmitInProgress.value = false;
      if (!err) {
        editQuestionDialogOpen.value = false;
        editingQuestionIndex.value = -1;
      }
    },
  });
}

function getQuizQuestionListEl(evt) {
  if (evt && evt.to) return evt.to;
  const wrap = quizQuestionListScrollRef.value;
  return wrap?.querySelector?.(".quiz-editor__question-list") || null;
}

/** Full list width + row height — must be captured before the in-list ghost collapses. */
function measureQuizDragRowMetrics(evt) {
  const row = evt?.item;
  const listEl = getQuizQuestionListEl(evt);
  if (!row || !listEl) return null;

  const rowRect = row.getBoundingClientRect();
  const listRect = listEl.getBoundingClientRect();
  const width = Math.round(listRect.width || rowRect.width);
  const height = Math.round(rowRect.height);

  if (!width || !height) return null;
  return { width, height };
}

/** Sortable appends the picked clone to `body`; size it to match the full question row in the list. */
function applyQuizDragFloaterStyles(metrics) {
  if (!metrics) return;

  document.querySelectorAll("body > .quiz-editor__q-row--picked-active").forEach((floater) => {
    floater.style.setProperty("--quiz-editor-drag-width", `${metrics.width}px`);
    floater.style.setProperty("--quiz-editor-ghost-gap-height", `${metrics.height}px`);
    floater.style.width = `${metrics.width}px`;
    floater.style.minWidth = `${metrics.width}px`;
    floater.style.maxWidth = `${metrics.width}px`;
    floater.style.height = "auto";
    floater.style.minHeight = `${metrics.height}px`;
    floater.style.display = "flex";
    floater.style.flexDirection = "row";
    floater.style.alignItems = "stretch";
    floater.style.boxSizing = "border-box";
  });
}

function scheduleQuizDragFloaterSync(metrics) {
  const sync = () => applyQuizDragFloaterStyles(metrics);
  requestAnimationFrame(() => {
    sync();
    requestAnimationFrame(sync);
  });
}

function applyQuizGhostPlaceholderMetrics(row, metrics) {
  if (!row || !metrics) return;
  row.style.setProperty("--quiz-editor-ghost-gap-height", `${metrics.height}px`);
  row.style.setProperty("--quiz-editor-drag-width", `${metrics.width}px`);
}

/** Collapse the in-list ghost at the origin slot; expand it when hovering another insertion index. */
function syncQuizQuestionGhostGap(evt) {
  const listEl = getQuizQuestionListEl(evt);
  if (!listEl || questionDragFromIndex.value < 0) return;

  const ghost = listEl.querySelector(".quiz-editor__q-row.quiz-editor__question-ghost");
  if (!ghost) return;

  const rows = [...listEl.querySelectorAll(".quiz-editor__q-row")];
  const ghostIndex = rows.indexOf(ghost);
  const atOrigin = ghostIndex === questionDragFromIndex.value;

  ghost.classList.toggle("quiz-editor__question-ghost--collapsed", atOrigin);
  ghost.classList.toggle("quiz-editor__question-ghost--expanded", !atOrigin);
}

function clearQuizQuestionGhostDom(evt) {
  const listEl = getQuizQuestionListEl(evt);
  if (!listEl) return;
  listEl.querySelectorAll(".quiz-editor__question-ghost--collapsed").forEach((el) => {
    el.classList.remove("quiz-editor__question-ghost--collapsed");
  });
  listEl.querySelectorAll(".quiz-editor__question-ghost--expanded").forEach((el) => {
    el.classList.remove("quiz-editor__question-ghost--expanded");
  });
}

function commitQuizQuestionReorder() {
  const snapshot = questionOrderSnapshot.value;
  const reordered = applyQuizOrderFromDom();
  if (reordered) {
    emit("reorder-questions", {
      questions: orderedRawQuestions.value.map(stripQuizRowKey),
    });
    return;
  }
  if (snapshot) {
    orderedRawQuestions.value = snapshot.map((q) => ({ ...q }));
  }
}

function getQuizRowFromSortableNode(node) {
  if (!node || typeof node.closest !== "function") return null;
  if (node.classList?.contains("quiz-editor__q-row")) return node;
  return node.closest(".quiz-editor__q-row");
}

function quizQuestionOrderChanged(before, after) {
  if (!before || !after || before.length !== after.length) return false;
  return before.some((item, i) => item.__quizRowKey !== after[i]?.__quizRowKey);
}

/** Apply row order from live DOM (Sortable is source of truth after drop). */
function applyQuizOrderFromDom() {
  const listEl = quizQuestionListScrollRef.value?.querySelector?.(".quiz-editor__question-list");
  const current = orderedRawQuestions.value;
  if (!listEl || !current.length) return false;

  const keys = [...listEl.querySelectorAll(".quiz-editor__q-row")]
    .map((row) => row.getAttribute("data-quiz-row-key"))
    .filter((k) => k != null && k !== "");

  if (keys.length !== current.length) return false;

  const byKey = new Map(current.map((q) => [String(q.__quizRowKey), q]));
  const next = keys.map((k) => byKey.get(String(k))).filter(Boolean);
  if (next.length !== current.length) return false;

  orderedRawQuestions.value = next.map((q) => ({ ...q }));
  return quizQuestionOrderChanged(questionOrderSnapshot.value, orderedRawQuestions.value);
}

function isQuizDropPointerInsideList() {
  const scrollEl = quizQuestionListScrollRef.value;
  if (!scrollEl) return true;
  const rect = scrollEl.getBoundingClientRect();
  const { x, y } = lastQuizDragPointer;
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function stopQuizSortableFallbackLoop() {
  const sortable = quizDraggableRef.value?._sortable;
  if (sortable?._loopId != null) {
    clearInterval(sortable._loopId);
    sortable._loopId = null;
  }
}

function cleanupQuizDragFloaters() {
  document.querySelectorAll("body > .quiz-editor__q-row--picked-active").forEach((node) => {
    node.remove();
  });
}

function cleanupQuizDragSession() {
  window.removeEventListener("dragover", onQuizWindowDragOver);
  stopQuizSortableFallbackLoop();
  cleanupQuizDragFloaters();
  setQuizDragSelectLock(false);
  clearDocumentTextSelection();
}

/** vuedraggable `@update` — list is already mutated; commit runs on `@end`. */
function onQuestionDragUpdate() {}

/** vuedraggable `move` — return -1 (above) / 1 (below) from pointer Y; DOM-only gap sync. */
function onQuizQuestionSortMove(evt, originalEvent) {
  if (originalEvent) {
    lastQuizDragPointer = { x: originalEvent.clientX, y: originalEvent.clientY };
  }
  syncQuizQuestionGhostGap(evt);
  applyQuizDragFloaterStyles(quizDragFloaterMetrics);

  if (!originalEvent) return true;

  const row = getQuizRowFromSortableNode(evt.related);
  if (!row || row === evt.dragged || row.classList.contains("quiz-editor__question-ghost")) {
    return true;
  }

  const rect = evt.relatedRect || row.getBoundingClientRect();
  const midY = rect.top + rect.height / 2;
  return originalEvent.clientY >= midY ? 1 : -1;
}

function onQuestionDragStart(evt) {
  questionDragActive.value = true;
  clearDocumentTextSelection();
  setQuizDragSelectLock(true);
  questionDragFromIndex.value = evt?.oldIndex ?? -1;
  questionOrderSnapshot.value = orderedRawQuestions.value.map((q) => ({ ...q }));
  clearQuizQuestionGhostDom(evt);

  quizDragFloaterMetrics = measureQuizDragRowMetrics(evt);
  applyQuizGhostPlaceholderMetrics(evt?.item, quizDragFloaterMetrics);
  scheduleQuizDragFloaterSync(quizDragFloaterMetrics);

  window.addEventListener("dragover", onQuizWindowDragOver, { passive: false });
  requestAnimationFrame(() => {
    syncQuizQuestionGhostGap(evt);
    applyQuizDragFloaterStyles(quizDragFloaterMetrics);
  });
}

function onQuestionDragEnd(evt) {
  stopQuizSortableFallbackLoop();
  cleanupQuizDragFloaters();

  const draggedRow = evt?.item;
  draggedRow?.style?.removeProperty?.("--quiz-editor-ghost-gap-height");
  draggedRow?.style?.removeProperty?.("--quiz-editor-drag-width");

  clearQuizQuestionGhostDom(evt);

  const snapshot = questionOrderSnapshot.value;
  const droppedOutside = !isQuizDropPointerInsideList();

  if (droppedOutside && snapshot) {
    orderedRawQuestions.value = snapshot.map((q) => ({ ...q }));
  } else {
    commitQuizQuestionReorder();
  }

  cleanupQuizDragSession();
  questionDragActive.value = false;
  questionDragFromIndex.value = -1;
  questionOrderSnapshot.value = null;
  quizDragFloaterMetrics = null;
}

onBeforeUnmount(() => {
  cleanupQuizDragSession();
  questionDragActive.value = false;
});

const hasQuestions = computed(() => orderedRawQuestions.value.length > 0);

const quizBulkMenuItems = computed(() => {
  const busy =
    deleteQuestionInProgress.value ||
    addQuestionSubmitInProgress.value ||
    editQuestionSubmitInProgress.value;
  return [
    {
      name: "Delete quiz",
      value: "delete-quiz",
      icon: "icons/dropdown/trash.svg",
      negative: true,
      destructive: true,
      disabled: busy,
    },
  ];
});

function onQuizBulkMenuClick(item) {
  if (
    deleteQuestionInProgress.value ||
    addQuestionSubmitInProgress.value ||
    editQuestionSubmitInProgress.value
  )
    return;
  const v = item?.value;
  if (v !== "delete-quiz") return;
  emit("quiz-bulk-action", { action: v });
}

const QUIZ_MAX_QUESTIONS_TOOLTIP = "You have reached the maximum number of questions";

const isQuizAtQuestionLimit = computed(
  () => orderedRawQuestions.value.length >= MAX_QUIZ_QUESTIONS
);

const isQuizEditorBusy = computed(
  () =>
    deleteQuestionInProgress.value ||
    addQuestionSubmitInProgress.value ||
    editQuestionSubmitInProgress.value
);

defineExpose({
  openAddQuestionModal,
  isQuizAtQuestionLimit,
  isQuizEditorBusy,
  quizBulkMenuItems,
  onQuizBulkMenuClick,
});

const extractAnswerText = (answer) => {
  if (typeof answer === "string") return answer;
  if (!answer || typeof answer !== "object") return "";

  return answer.answer || answer.text || answer.answer_text || answer.a_text || "";
};

function answerIsMarkedCorrect(answer, index, expectedIndexes, expectedAnswerTexts, answerText) {
  if (typeof answer === "object" && answer !== null) {
    if (answer.is_correct === true) return true;
    if (answer.isCorrect === 1 || answer.isCorrect === true || Number(answer.isCorrect) === 1)
      return true;
  }
  if (expectedIndexes.includes(index)) return true;
  if (expectedAnswerTexts.includes(answerText)) return true;
  return false;
}

const normalizedQuestions = computed(() => {
  if (!hasQuestions.value) return [];

  return orderedRawQuestions.value.map((question) => {
    const answerList = Array.isArray(question.answers)
      ? question.answers
      : Array.isArray(question.q_answers)
        ? question.q_answers
        : Array.isArray(question.options)
          ? question.options
          : [];

    const expectedIndexes = Array.isArray(question.correctAnswers)
      ? question.correctAnswers.filter((item) => Number.isInteger(item))
      : [];

    const expectedAnswerTexts = Array.isArray(question.correctAnswers)
      ? question.correctAnswers.filter((item) => typeof item === "string")
      : [];

    return {
      __quizRowKey: question.__quizRowKey,
      q_no: resolveRowQNo(question),
      text: question.question || question.text || question.q_text || "",
      answers: answerList.map((answer, index) => {
        const answerText = extractAnswerText(answer);
        const isExpected = answerIsMarkedCorrect(
          answer,
          index,
          expectedIndexes,
          expectedAnswerTexts,
          answerText
        );

        return {
          text: answerText,
          isExpected,
        };
      }),
    };
  });
});
</script>

<style scoped>
.quiz-editor {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.quiz-editor__empty-state {
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  display: flex;
  padding: 35px 24px;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.quiz-editor__empty-inner {
  display: flex;
  max-width: 480px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.quiz-editor__empty-icon {
  border-radius: 10px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  display: flex;
  width: 48px;
  height: 48px;
  padding: 12px;
  justify-content: center;
  align-items: center;
}

.quiz-editor__empty-title {
  margin: 0;
  color: #101828;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.quiz-editor__empty-description {
  margin: 0;
  color: #667085;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.quiz-editor__empty-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
}

/* —— Filled state —— */
.quiz-editor__filled {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.quiz-editor__quiz-title {
  margin: 0;
  padding: 0 2px;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.quiz-editor__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.quiz-editor__toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.quiz-editor__disabled-btn-tooltip-wrap {
  display: inline-flex;
}

.quiz-editor__toolbar-menu {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.quiz-editor__toolbar-menu :deep(.ex-menu-icon) {
  margin: 0;
  padding: 10px 12px;
}

.quiz-editor__toolbar-menu :deep(.ex-menu-icon:hover) {
  margin: 0;
  padding: 10px 12px;
}

.quiz-editor__max-questions-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid #e4e7ec;
  background: #ffffff;
}

.quiz-editor__max-questions-banner-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quiz-editor__max-questions-banner-text {
  margin: 0;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.quiz-editor__question-list-scroll {
  max-height: min(56vh, 520px);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.quiz-editor__question-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
}

.quiz-editor__question-list--dragging {
  cursor: grabbing;
}

/* Prevent blue text selection while dragging rows (cursor sweep across labels). */
.quiz-editor__question-list-scroll,
.quiz-editor__question-list-scroll .quiz-editor__q-row,
.quiz-editor__question-list-scroll .quiz-editor__q-row * {
  -webkit-user-select: none;
  user-select: none;
}

.quiz-editor__question-list--dragging,
.quiz-editor__question-list--dragging * {
  -webkit-user-select: none !important;
  user-select: none !important;
}

/* Card row: grip strip + header + body */
.quiz-editor__q-row {
  display: flex;
  align-items: stretch;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #f9fafb;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  cursor: grab;
  -webkit-user-select: none;
  user-select: none;
  /* Do not transition `transform` — Sortable uses it to slide rows up/down while dragging. */
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;
}

.quiz-editor__question-list--dragging .quiz-editor__q-row {
  cursor: grabbing;
}

.quiz-editor__q-row--readonly {
  cursor: default;
}

.quiz-editor__q-row:last-child {
  margin-bottom: 0;
}

.quiz-editor__drag-strip {
  flex: 0 0 32px;
  border-right: 2px solid #e4e7ec;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 8px;
  box-sizing: border-box;
}

.quiz-editor__drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 0 4px;
  margin: 0;
  border: none;
  background: transparent;
  cursor: grab;
}

.quiz-editor__drag-handle:active {
  cursor: grabbing;
}

.quiz-editor__drag-grip {
  display: block;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  pointer-events: none;
}

.quiz-editor__card {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: none;
  border-radius: 0;
  background: #f9fafb;
}

.quiz-editor__card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
}

.quiz-editor__card-label {
  color: #475467;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
}

.quiz-editor__card-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.quiz-editor__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.quiz-editor__icon-btn:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

.quiz-editor__icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quiz-editor__icon-btn-img {
  display: block;
  flex-shrink: 0;
  pointer-events: none;
}

.quiz-editor__icon-btn--edit {
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
}

.quiz-editor__icon-btn--edit:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #bfc8d4;
}

.quiz-editor__icon-btn--delete {
  background-color: #fef3f2;
  border: 1px solid #fecdca;
}

.quiz-editor__icon-btn--delete:hover:not(:disabled) {
  background-color: #fee4e2;
  border-color: #fda29b;
}

.quiz-editor__card-divider {
  flex-shrink: 0;
  height: 2px;
  margin: 0;
  border: 0;
  background: #e4e7ec;
}

.quiz-editor__card-body {
  flex: 1 1 auto;
  background: #f9fafb;
  padding: 10px 12px 12px;
}

.quiz-editor__card-question {
  margin: 0 0 8px;
  padding: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.quiz-editor__card-answers {
  list-style: none;
  margin: 0;
  padding: 0 0 0 6px;
}

.quiz-editor__card-answer {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 2px 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #344054;
}

.quiz-editor__card-answer-check {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 22px;
  margin-top: 1px;
}

.quiz-editor__card-answer-spacer {
  flex: 0 0 18px;
  width: 18px;
  height: 22px;
}

.quiz-editor__card-answer-text {
  flex: 1 1 auto;
  min-width: 0;
}
::v-deep .v-input--switch--inset .v-input--selection-controls__input {
  width: 38px;
}
</style>

<style>
/*
 * Unscoped: `drag-class` is on the body-appended fallback clone; `ghost-class` stays in the list.
 */
.quiz-editor__q-row.quiz-editor__q-row--picked-active {
  cursor: grabbing !important;
  position: fixed;
  z-index: 100001;
  -webkit-user-select: none !important;
  user-select: none !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: stretch !important;
  width: var(--quiz-editor-drag-width) !important;
  min-width: var(--quiz-editor-drag-width) !important;
  max-width: var(--quiz-editor-drag-width) !important;
  min-height: var(--quiz-editor-ghost-gap-height, auto) !important;
  box-sizing: border-box !important;
  border: 2px solid #c026d3 !important;
  background-color: #fdf4ff !important;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.95),
    0 12px 32px rgba(192, 38, 211, 0.18),
    0 4px 12px rgba(16, 24, 40, 0.08) !important;
  opacity: 1 !important;
  pointer-events: none;
  overflow: hidden !important;
}

.quiz-editor__q-row.quiz-editor__q-row--picked-active .quiz-editor__drag-strip {
  flex: 0 0 32px !important;
}

.quiz-editor__q-row.quiz-editor__q-row--picked-active .quiz-editor__card {
  flex: 1 1 auto !important;
  min-width: 0 !important;
  width: auto !important;
}

.quiz-editor__q-row.quiz-editor__q-row--picked-active .quiz-editor__card-head {
  background: #ffffff !important;
}

/* In-list placeholder: collapsed at origin so remaining cards reflow (2, 3, 4, 5…). */
.quiz-editor__q-row.quiz-editor__question-ghost.quiz-editor__question-ghost--collapsed {
  flex: 0 0 auto !important;
  height: 0 !important;
  min-height: 0 !important;
  max-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  overflow: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

/* Expanded gap at the hovered insertion index (no height transition — Sortable drives layout). */
.quiz-editor__q-row.quiz-editor__question-ghost.quiz-editor__question-ghost--expanded {
  cursor: grabbing !important;
  flex: 0 0 auto;
  height: var(--quiz-editor-ghost-gap-height, 120px);
  min-height: var(--quiz-editor-ghost-gap-height, 120px);
  max-height: var(--quiz-editor-ghost-gap-height, 120px);
  opacity: 1 !important;
  background-color: #f2f4f7 !important;
  border: 2px dashed #98a2b3 !important;
  border-radius: 10px !important;
  box-shadow: none !important;
  z-index: 2;
  transition: none !important;
}

.quiz-editor__q-row.quiz-editor__question-ghost.quiz-editor__question-ghost--expanded > * {
  visibility: hidden;
}
</style>
